import React, { useState, useEffect } from 'react';
import toast from 'react-hot-toast';
import {
    BreadCrumbs,
    CommonModal as UpdateModal,
    CommonModal as DeleteModal,
    Input,
    PrimaryButton,
    SecondaryButton,
    PageTitleWithToolTip,
    AddButton,
    DataGrid
} from '../Common';
import Routes from '../../routes';
import { fetchFilterValues } from '../../utils';
import { Grey1, Grey2, Grey3 } from '../../color_utils';
import { Grid, Typography, Tooltip } from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import DeleteConfirmation from '../../Icons/img_delete_confirmation.svg';
import { select_commonCellProps } from "../Common/DataGrid/utils";
import { GridActionsCellItem } from "@mui/x-data-grid-pro";

const toolTipText = `
    Manage regions and divisions on locations in the account.
    A region/division is used to categorize certain locations into
    a group based on where they are located and/or which business
    line they fall under in the company.
`;

const Associations = () => {
    const [modalData, setModalData] = useState({});
    const [refresh, setRefresh] = useState(false);
    const [filterOptions, setFilterOptions] = useState([]);

    const columns = [
        {
            field: 'label',
            headerName: 'Region / Division Name',
            ...select_commonCellProps,
            renderCell: ({ row: { id, label } }) => (
                <span key={id}>
                    {label}
                </span>
            )
        },
        {
            field: 'numberOfLocations',
            headerName: 'Number of Locations',
            flex: 1,
            filterable: false
        },
        {
            type: 'actions',
            field: 'Quick Actions',
            width: 100,
            getActions: ({ row }) => [
                <GridActionsCellItem
                    icon={
                        <Tooltip
                            title="Edit Region / Division"
                        >
                            <EditIcon sx={{ color: Grey2 }} />
                        </Tooltip>
                    }
                    onClick={() =>  handleEdit(row)}
                />,
                <GridActionsCellItem
                    icon={
                        <Tooltip
                            title="Delete Region / Division"
                        >
                            <DeleteIcon sx={{ color: Grey2 }} />
                        </Tooltip>
                    }
                    onClick={() => handleDelete(row)}
                />
            ]
        },
    ];

    const handleCreate = () => setModalData({
        editOpen: true,
        header: "Add Region / Division",
        regionName: "",
    });

    const resetState = () => {
        setModalData({});
        return setRefresh(prev => !prev);
    };

    const handleError = (error = '') => {
        toast.error('Something went wrong. Please try again.', { duration: 6000 });
        return console.log('Error', error);
    };

    const handleEdit = row => (
        setModalData({
            editOpen: true,
            header: "Edit Region / Division",
            regionName: row.label,
            id: row.id,
            enabled: row.enabled,
        })
    );

    const handleDelete = row => (
        setModalData({
            deleteOpen: true,
            regionName: row.label,
            id: row.id,
        })
    );

    const createRegion = async () => {
        const instanceData = { name: modalData.regionName };
        await fetch(
            Routes.Instance.CreateInstanceAssociation(),
            {
                method : "POST",
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(instanceData)
            }
        )
            .then(res => {
                if (res.status === 200 ) {
                    toast.success("The region / division has been successfully added.", { duration: 5000 });
                } else {
                    toast.error('Error creating region / division. Please try again.');
                    console.log('Response', res);
                }
            })
            .finally(() => resetState())
            .catch(err => handleError(err));
    };

    const submitData = async (instanceEnabled) => {
        const instanceData = { id: modalData.id, name: modalData.regionName, enabled: instanceEnabled };
        await fetch(
            Routes.Instance.UpdateInstanceAssociation(instanceData.id),
            {
                method : "POST",
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(instanceData)
            }
        )
        .then((res) => {
            if (res.status === 200 ) {
                const successType = instanceEnabled ? 'edited' : 'deleted';
                toast.success(`The region / division has been successfully ${successType}.`);
            } else {
                toast.error('Error updating Instance. Please try again.', { duration: 5000 });
                console.log('Response', res);
            }
        })
        .finally(() => resetState())
        .catch(err => handleError(err));
    };

    const fetchData = async () => {
        const data = await fetchFilterValues(Routes.Instance.TableFilters());
        setFilterOptions(data);
    };

    useEffect(() => { fetchData() }, []);

    return (
        <>
            <BreadCrumbs
                path={[
                    { text: "Account Details", to: "/Account" },
                    { text: "Manage Regions and Divisions" }
                ]}
            />

            <Grid container spacing={2}>
                <Grid item xs={6}>
                    <PageTitleWithToolTip
                        pageTitle="Manage Regions and Divisions"
                        toolTipText={toolTipText}
                    />
                </Grid>
                <Grid item align="right" sx={{ marginLeft: "auto" }}>
                    <AddButton
                        text="Add Region/Divison"
                        onClick={handleCreate}
                    />
                </Grid>
                <Grid item xs={6} sx={{ marginTop: '1%' }}>
                    <DataGrid
                        url={Routes.Instance.GetUserInstanceAssociationsForTable()}
                        exportButtonURL={Routes.Instance.Export()}
                        filterOptions={filterOptions}
                        noResultText="Oops...It is empty here..."
                        noResultText2="Associate a new Region / Division to get started."
                        refresh={refresh}
                        rowCount={5}
                        columns={columns}
                        exportFileName="Regions & Divisions"
                    />
                </Grid>
            </Grid>

            <UpdateModal
                open={modalData.editOpen}
                close={() => setModalData(prev => ({ ...prev, editOpen: false }))}
                header={modalData.header}
                width="26%"
                headerTopMargin={7}
            >
                <Grid container spacing={2} justifyContent="center" mt={0}>
                    <Grid item xs={10}>
                        <Input
                            required
                            fullWidth
                            variant="outlined"
                            label="Region/Division Name"
                            value={modalData.regionName}
                            onChange={e => setModalData(prev => ({ ...prev, regionName: e.target.value }))}
                        />
                    </Grid>
                    <Grid item xs={10} mb={4}>
                        <PrimaryButton
                            fullWidth
                            useLargeButton
                            buttonText={modalData.id ? 'Edit' : 'Add'}
                            onClick={() => modalData.id ? submitData(true) : createRegion()}
                        />
                    </Grid>
                </Grid>
            </UpdateModal>

            <DeleteModal
                open={modalData.deleteOpen}
                close={() => setModalData(prev => ({ ...prev, deleteOpen: false }))}
            >
                <Grid container spacing={2}>
                    <Grid item xs={12} align='center'>
                        <img
                            style={{ paddingTop: '1%', paddingBottom: '5%' }}
                            src={DeleteConfirmation}
                            width="275px"
                            height="250px"
                            alt="Are you sure you want to delete this region/division?"
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <Typography
                            variant="h5"
                            sx={{ '&.MuiTypography-root': { color: Grey1 } }}
                            align='center'
                        >
                            Delete this Region/Division ?
                        </Typography>
                    </Grid>
                    <Grid item xs={12}>
                        <Typography
                            variant="h5"
                            sx={{ '&.MuiTypography-root': { fontSize: 14, color: Grey3 } }}
                            align='center'
                        >
                            It will be permanently lost.
                        </Typography>
                    </Grid>
                    <Grid item xs={12} align='center'>
                        <PrimaryButton
                            useLargeButton
                            buttonText='Yes, Delete'
                            onClick={() => submitData(false)}
                            width={75}
                        />
                    </Grid>
                    <Grid item xs={12} align='center'>
                        <SecondaryButton
                            useLargeButton
                            buttonText='Cancel'
                            onClick={() => setModalData(prev => ({ ...prev, deleteOpen: false }))}
                            width={75}
                        />
                    </Grid>
                </Grid>
            </DeleteModal>
        </>
    );
};

export default Associations;
